<script lang="ts" setup>
import { useCategoryStore } from '~/store/categoryStore'
import { useStoreConfig } from '~/store/storeConfig'
import { useLanguageStore } from '~/store/language'
import CookiesEnum from '~/enums/CookiesEnum'
import { useAnalyticalDataTracking } from '~/composables/analytics/useAnalyticalDataTracking'
import type { Meta } from '@unhead/schema'
import shouldNotIndexRoute from '~/utils/seo/shouldNotIndexRoute'
import { useUtm } from '~/composables/useUtm'
import { useCategoryWatch } from '~/composables/useCategoryWatch'
import { useQueryKeyWatch } from '~/composables/useQueryKeyWatch'
import useSiteMetadata from '~/composables/useSiteMetadata'
import scrollToTop from '~/utils/scrollToTop'
import { debounce } from 'perfect-debounce'
import TtlEnum from '~/enums/TtlEnum'
import checkScreenSize from '~/utils/checkScreenSize'
import { useProductContentStore } from '~/store/productContentStore'
import { ThemeEnum } from '~/enums/Region'

const languageManager = LanguageManager()
const locale = useState<string>('locale.setting')

const nuxtApp = useNuxtApp()

// scrollToTop is not yet supported in Nuxt3. https://v3.nuxtjs.org/migration/component-options#scrolltotop
nuxtApp.hook('page:transition:finish', () => {
  scrollToTop(false)
})

const { isAlt2, theme } = useTheme()

if (theme !== ThemeEnum.SHIPZEE) {
  definePageMeta({
    colorMode: 'light',
  })
}

const languageStore = useLanguageStore()
const storeConfig = useStoreConfig()
const contentStore = useProductContentStore()
await storeConfig.getStoreConfig()

const { metaImage, metaTitle, metaType, metaUrl, siteName, verifyPaysera } = useSiteMetadata()

const utm = useUtm()
const categoryStore = useCategoryStore()
const categoryWatch = useCategoryWatch()
const queryKeyWatch = useQueryKeyWatch()
const currencyCookie = useCookie(CookiesEnum.CurrencyId)

languageManager.initWatch()
queryKeyWatch.setQueryKeys(locale.value)

queryKeyWatch.initWatch()
categoryWatch.initWatch()

if (currencyCookie.value) {
  storeConfig.initCurrencyById(Number(currencyCookie.value))
}

const debouncedResize = debounce(() => {
  checkScreenSize()
}, 500)

onBeforeMount(() => {
  if (typeof window !== 'undefined') {
    window?.addEventListener('resize', debouncedResize)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window?.removeEventListener('resize', debouncedResize)
  }
})

checkScreenSize()

watch(
  locale,
  (newLocale) => {
    languageStore.setLanguage(newLocale)
    if (!useRoute()?.query.s) {
      const sortType = storeConfig.getSite?.languages?.find(
        (lang) => lang.iso_code.toLowerCase() === newLocale.toLowerCase()
      )?.pivot.mp_default_sort

      contentStore.setSortType(sortType)
    }
  },
  {
    immediate: true,
  }
)

watch(
  () => storeConfig.getIsMobile,
  () => {
    reloadNuxtApp({
      force: true,
    })
  }
)

onBeforeMount(async () => {
  await categoryStore.getCategoriesByLocale(locale.value)
  await categoryStore.getFlagsByLocale(locale.value)
  await utm.init()
  await useAnalyticalDataTracking().trackPageLoad()
})

useRouter()?.afterEach(() => {
  useAnalyticalDataTracking().trackPageView(null)
})

const route = useRoute()
let robotMeta: Array<Meta> = []

if (shouldNotIndexRoute(route)) {
  robotMeta = [
    {
      name: 'robots',
      content: 'noindex',
    },
  ]
}

const meta = [
  {
    name: 'og:title',
    content: metaTitle.value,
  },
  {
    name: 'og:url',
    content: metaUrl.value,
  },
  {
    name: 'og:image',
    content: metaImage.value,
  },
  {
    name: 'og:image:secure_url',
    content: metaImage.value,
  },
  {
    name: 'og:type',
    content: metaType.value,
  },
  ...(verifyPaysera.value
    ? [
        {
          name: 'verify-paysera',
          content: verifyPaysera.value,
        },
      ]
    : []),
]

useHead(() => ({
  title: metaTitle.value,
  titleTemplate: (title) => {
    if (isAlt2) {
      return `${siteName.value ? `${siteName.value} | ` : ''}${title}`
    }

    return `${title}${siteName.value ? ` | ${siteName.value}` : ''}`
  },
  meta: [...meta, ...robotMeta],
  link: [
    {
      rel: 'canonical',
      href: metaUrl.value,
    },
  ],
}))

const canViewTranslations = useCookie(CookiesEnum.CanViewTranslations, {
  maxAge: TtlEnum.OneDay,
  default: () => '0',
  watch: 'shallow',
})
</script>

<template>
  <TranslationKeyToggler v-if="canViewTranslations" />
  <div>
    <ModalUnsupportedBrowser />
    <NuxtLoadingIndicator :height="5" />
    <NuxtPage />
    <LoadingOverlay />
    <ModalUnpaidOrdersAlert />
    <UModals />
    <div id="modal-root"></div>
  </div>
</template>

<style lang="scss">
html {
  scroll-behavior: smooth;
  scroll-padding-top: 2rem;
}

:root {
  --h1--font-size: theme('fontSize.7xl');
  --h2--font-size: theme('fontSize.6xl');
  --h3--font-size: theme('fontSize.5xl');
  --h4--font-size: theme('fontSize.3xl');
  --h5--font-size: theme('fontSize.2xl');
  --h6--font-size: theme('fontSize.xl');
  --heading--font-family: theme('vars.fontFamily.primary');
  --heading--margin-top: 0;

  // Formkit
  --fk-color-primary: theme('colors.brand.primary');
  --heading--color: theme('colors.black');
  --body--color: theme('colors.black');
  --fk-color-border: theme('colors.gray2');
}

.dark {
  --fk-color-border: theme('colors.dmGray4');
  --heading--color: theme('colors.white');
  --body--color: theme('colors.white');
}

:root .alt {
  --heading--font-family: theme('vars.fontFamily.heading');
  --fk-color-primary: theme('colors.blue');
  --fk-color-border-focus: theme('colors.blue');
  --fk-border-box-shadow-focus: 0 0 0 2px theme('colors.blue');
}

.dark :root.alt {
  --fk-color-primary: theme('colors.blue');
}
:root .ae {
  --fk-border-box-shadow: 0 0 0 1px theme('colors.paleYellow');
}
</style>
